import type { ReactNode } from 'react';

import { Badge } from '@playbook/atoms/badge';

import type { Action, Key, Organization } from '@portal/pkg/api/types';
import type { KeyStatus } from '@portal/pkg/api/types';
import { Notice } from '@portal/views/_shared/notice';

/**
 *	Whether the API said the caller may do this.
 *
 *  Absent actions mean no — a payload that never carried them is a caller who
 *	was told nothing, and guessing from capabilities here would be a second,
 *	divergent copy of the server's rules.
 */
export function can(
	subject: Nullable<Key | Organization> | undefined,
	action: Action
): boolean {
	return subject?.allowedActions?.includes(action) ?? false;
}

/**
 *	What a page shows instead of a form the server would refuse.
 *
 *	The affordances that lead here are already hidden, so reaching it means a
 *	deep link, a stale tab, or access that was taken away since the page
 *	loaded — all of which deserve a sentence rather than a 403 on submit.
 */
export function NotPermitted({ what }: { what: string }): ReactNode {
	return (
		<Notice variant="information" title={`You cannot ${what}`}>
			This needs write access to the organization&rsquo;s API keys. An
			administrator can grant it on your role.
		</Notice>
	);
}

/** How close to its expiry a live key starts being worth warning about. */
const ExpiryWarningDays = 14 as const;

const DaySeconds = 24 * 60 * 60;

const StatusLabels: Record<KeyStatus, string> = {
	active: 'Active',
	expired: 'Expired',
	revoked: 'Revoked',
};

export function isActive(key: Key): boolean {
	return key.status === 'active';
}

export function daysUntilExpiry(key: Key): number {
	return Math.ceil((key.expiresAt - Date.now() / 1000) / DaySeconds);
}

export function expiresSoon(key: Key): boolean {
	return isActive(key) && daysUntilExpiry(key) <= ExpiryWarningDays;
}

/**
 *	The one badge a key wears. A key close to its expiry is still active — the
 *	server says so — so the warning replaces the label rather than joining it.
 */
export function KeyStatusBadge({ forKey }: { forKey: Key }): ReactNode {
	if (expiresSoon(forKey)) {
		const days = daysUntilExpiry(forKey);

		return (
			<Badge
				variant="warning"
				label={days <= 1 ? 'Expires today' : `${days} days left`}
			/>
		);
	}

	return (
		<Badge
			variant={forKey.status === 'revoked' ? 'error' : 'neutral'}
			label={StatusLabels[forKey.status]}
		/>
	);
}

/** A key and everything it replaced, newest generation first. */
export function generations(key: Key): Key[] {
	const chain: Key[] = [];

	for (let link: Optional<Key> = key; link; link = link.rotatedFrom) {
		chain.push(link);
	}

	return chain;
}

/**
 *	The listing returns only the head of each chain, with everything it
 *	replaced nested underneath, so a predecessor is reachable but not findable
 *	without walking.
 */
export function findKey(keys: Key[], id: Optional<string>): Optional<Key> {
	if (!id) {
		return undefined;
	}

	return keys.flatMap(generations).find((key) => key.id === id);
}

export function formatDate(unixSeconds: number): string {
	return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	});
}

export function formatDateTime(unixSeconds: number): string {
	return new Date(unixSeconds * 1000).toLocaleString(undefined, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
}
